import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { buildApiUrl, apiConfig } from '../config/api';

// Journey Stage Configuration Types
interface JourneyStageCriteria {
  checkInsCount?: number;
  groupsCount?: number;
  servicesCount?: number;
  totalGiving?: number;
  registrationsCount?: number;
  membershipType?: string[];
  eventsAttended?: number;
  servingHistory?: boolean;
  lastActivityDays?: number;
}

interface JourneyStage {
  id: string;
  name: string;
  description: string;
  order: number;
  color: string;
  criteria: JourneyStageCriteria;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

interface JourneyStageConfig {
  id: string;
  name: string;
  description: string;
  stages: JourneyStage[];
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

const JourneyStageAdmin: React.FC = () => {
  const { user: _user } = useAuth(); // Prefix with underscore to indicate intentionally unused
  const [configurations, setConfigurations] = useState<JourneyStageConfig[]>([]);
  const [activeConfig, setActiveConfig] = useState<JourneyStageConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState<JourneyStageConfig | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Form state for creating/editing
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    stages: [] as JourneyStage[]
  });

  const fetchConfigurations = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('DEBUG: Starting to fetch journey stage data...');
      console.log('DEBUG: Configurations URL:', buildApiUrl(apiConfig.endpoints.journeyStages.configurations));
      console.log('DEBUG: Active URL:', buildApiUrl(apiConfig.endpoints.journeyStages.active));

      const [configsResponse, activeResponse] = await Promise.all([
        fetch(buildApiUrl(apiConfig.endpoints.journeyStages.configurations)),
        fetch(buildApiUrl(apiConfig.endpoints.journeyStages.active))
      ]);

      console.log('DEBUG: configsResponse status:', configsResponse.status);
      console.log('DEBUG: activeResponse status:', activeResponse.status);

      if (!configsResponse.ok) {
        console.error('DEBUG: Configurations response not OK:', configsResponse.statusText);
        setError(`Failed to fetch configurations: ${configsResponse.statusText}`);
        return;
      }

      const configsData = await configsResponse.json();
      const activeData = activeResponse.ok ? await activeResponse.json() : null;

      console.log('DEBUG: configsData:', configsData);
      console.log('DEBUG: configsData.data:', configsData.data);
      console.log('DEBUG: configsData.data.configurations:', configsData.data?.configurations);
      console.log('DEBUG: activeData:', activeData);

      if (configsData.success && configsData.data && Array.isArray(configsData.data.configurations)) {
        console.log('DEBUG: Setting configurations to:', configsData.data.configurations);
        setConfigurations(configsData.data.configurations);
      } else {
        console.error('DEBUG: Invalid configurations data structure:', configsData);
        setConfigurations([]); // Ensure it's always an array
        setError('Invalid data format received from server');
      }

      if (activeData && activeData.success && activeData.data && activeData.data.configuration) {
        console.log('DEBUG: Setting active config to:', activeData.data.configuration);
        setActiveConfig(activeData.data.configuration);
      }
    } catch (err) {
      console.error('DEBUG: Fetch error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
      setConfigurations([]); // Ensure it's always an array even on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigurations();
  }, []);

  const handleCreateConfig = async () => {
    try {
      const response = await fetch(buildApiUrl(apiConfig.endpoints.journeyStages.configurations), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          stages: formData.stages,
          isActive: false
        })
      });

      const data = await response.json();
      if (data.success) {
        setShowCreateForm(false);
        setFormData({ name: '', description: '', stages: [] });
        fetchConfigurations();
      } else {
        setError(data.error?.message || 'Failed to create configuration');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleUpdateConfig = async () => {
    if (!editingConfig) return;

    try {
      const response = await fetch(buildApiUrl(apiConfig.endpoints.journeyStages.update(editingConfig.id)), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          stages: formData.stages,
          isActive: editingConfig.isActive
        })
      });

      const data = await response.json();
      if (data.success) {
        setEditingConfig(null);
        setIsEditing(false);
        setFormData({ name: '', description: '', stages: [] });
        fetchConfigurations();
      } else {
        setError(data.error?.message || 'Failed to update configuration');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleEditConfig = (config: JourneyStageConfig) => {
    setEditingConfig(config);
    setIsEditing(true);
    setFormData({
      name: config.name,
      description: config.description,
      stages: [...config.stages]
    });
    setShowCreateForm(true);
  };

  const handleActivateConfig = async (id: string) => {
    try {
      const response = await fetch(buildApiUrl(apiConfig.endpoints.journeyStages.activate(id)), {
        method: 'POST'
      });

      const data = await response.json();
      if (data.success) {
        fetchConfigurations();
      } else {
        setError(data.error?.message || 'Failed to activate configuration');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleDeleteConfig = async (id: string) => {
    if (!confirm('Are you sure you want to delete this configuration?')) return;

    try {
      const response = await fetch(buildApiUrl(apiConfig.endpoints.journeyStages.delete(id)), {
        method: 'DELETE'
      });

      const data = await response.json();
      if (data.success) {
        fetchConfigurations();
      } else {
        setError(data.error?.message || 'Failed to delete configuration');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const addStage = () => {
    const newStage: JourneyStage = {
      id: `stage-${Date.now()}`,
      name: '',
      description: '',
      order: formData.stages.length,
      color: '#6B7280',
      criteria: {},
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    setFormData(prev => ({
      ...prev,
      stages: [...prev.stages, newStage]
    }));
  };

  const updateStage = (index: number, field: keyof JourneyStage, value: any) => {
    setFormData(prev => ({
      ...prev,
      stages: prev.stages.map((stage, i) => 
        i === index ? { ...stage, [field]: value } : stage
      )
    }));
  };

  const updateStageCriteria = (index: number, field: keyof JourneyStageCriteria, value: any) => {
    setFormData(prev => ({
      ...prev,
      stages: prev.stages.map((stage, i) => 
        i === index ? { 
          ...stage, 
          criteria: { ...stage.criteria, [field]: value }
        } : stage
      )
    }));
  };

  const removeStage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      stages: prev.stages.filter((_, i) => i !== index)
    }));
  };

  const cancelForm = () => {
    setShowCreateForm(false);
    setEditingConfig(null);
    setIsEditing(false);
    setFormData({ name: '', description: '', stages: [] });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading journey stage configurations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Configurations</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchConfigurations}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-3xl font-bold text-gray-900">Journey Stage Configuration</h1>
            <p className="text-gray-600 mt-1">Define custom journey stages and criteria for member progression</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Active Configuration */}
        {activeConfig && (
          <div className="bg-white rounded-lg shadow mb-8">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Active Configuration</h2>
              <p className="text-sm text-gray-600 mt-1">{activeConfig.description}</p>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{activeConfig.name}</h3>
                  <p className="text-sm text-gray-500">{activeConfig.stages?.length || 0} stages defined</p>
                </div>
                <div className="flex space-x-2">
                  {activeConfig.stages?.map((stage, _index) => (
                    <span
                      key={stage.id}
                      className="px-3 py-1 text-sm font-medium rounded-full"
                      style={{ backgroundColor: stage.color + '20', color: stage.color }}
                    >
                      {stage.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Configuration List */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">All Configurations</h2>
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create New Configuration
            </button>
          </div>
          <div className="p-6">
{(() => {
              console.log('DEBUG: configurations state:', configurations);
              console.log('DEBUG: configurations type:', typeof configurations);
              console.log('DEBUG: configurations.length:', configurations?.length);
              console.log('DEBUG: Array.isArray(configurations):', Array.isArray(configurations));
              return null;
            })()}
            {!Array.isArray(configurations) || configurations.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No configurations found. Create your first one!</p>
            ) : (
              <div className="space-y-4">
                {configurations.map((config) => (
                  <div key={config.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{config.name}</h3>
                        <p className="text-sm text-gray-500">{config.description}</p>
                        <div className="flex space-x-2 mt-2">
                          {config.stages.map((stage) => (
                            <span
                              key={stage.id}
                              className="px-2 py-1 text-xs font-medium rounded-full"
                              style={{ backgroundColor: stage.color + '20', color: stage.color }}
                            >
                              {stage.name}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditConfig(config)}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                        >
                          Edit
                        </button>
                        {!config.isActive && (
                          <button
                            onClick={() => handleActivateConfig(config.id)}
                            className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
                          >
                            Activate
                          </button>
                        )}
                        {config.isActive && (
                          <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm font-medium">
                            Active
                          </span>
                        )}
                        {config.id !== 'default' && (
                          <button
                            onClick={() => handleDeleteConfig(config.id)}
                            className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Create/Edit Form */}
        {showCreateForm && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {isEditing ? 'Edit Configuration' : 'Create New Configuration'}
              </h2>
            </div>
            <div className="p-6">
              <div className="space-y-6">
                {/* Basic Info */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Configuration Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Grace Fellowship Journey Stages"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Describe this configuration..."
                  />
                </div>

                {/* Stages */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Journey Stages</h3>
                    <button
                      onClick={addStage}
                      className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                    >
                      Add Stage
                    </button>
                  </div>

                  <div className="space-y-4">
                    {formData.stages.map((stage, index) => (
                      <div key={stage.id} className="border rounded-lg p-4">
                        <div className="flex justify-between items-start mb-4">
                          <h4 className="text-md font-medium text-gray-900">Stage {index + 1}</h4>
                          <button
                            onClick={() => removeStage(index)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Remove
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Stage Name
                            </label>
                            <input
                              type="text"
                              value={stage.name}
                              onChange={(e) => updateStage(index, 'name', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="e.g., Leader, Engaged Member"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Color
                            </label>
                            <input
                              type="color"
                              value={stage.color}
                              onChange={(e) => updateStage(index, 'color', e.target.value)}
                              className="w-full h-10 border border-gray-300 rounded-md"
                            />
                          </div>

                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Description
                            </label>
                            <input
                              type="text"
                              value={stage.description}
                              onChange={(e) => updateStage(index, 'description', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Describe this stage..."
                            />
                          </div>
                        </div>

                        {/* Criteria */}
                        <div className="mt-4">
                          <h5 className="text-sm font-medium text-gray-700 mb-2">Criteria</h5>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Min Check-ins
                              </label>
                              <input
                                type="number"
                                value={stage.criteria.checkInsCount || ''}
                                onChange={(e) => updateStageCriteria(index, 'checkInsCount', e.target.value ? parseInt(e.target.value) : undefined)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                placeholder="0"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Min Groups
                              </label>
                              <input
                                type="number"
                                value={stage.criteria.groupsCount || ''}
                                onChange={(e) => updateStageCriteria(index, 'groupsCount', e.target.value ? parseInt(e.target.value) : undefined)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                placeholder="0"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Min Services
                              </label>
                              <input
                                type="number"
                                value={stage.criteria.servicesCount || ''}
                                onChange={(e) => updateStageCriteria(index, 'servicesCount', e.target.value ? parseInt(e.target.value) : undefined)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                placeholder="0"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Min Giving ($)
                              </label>
                              <input
                                type="number"
                                value={stage.criteria.totalGiving || ''}
                                onChange={(e) => updateStageCriteria(index, 'totalGiving', e.target.value ? parseFloat(e.target.value) : undefined)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                placeholder="0"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Min Registrations
                              </label>
                              <input
                                type="number"
                                value={stage.criteria.registrationsCount || ''}
                                onChange={(e) => updateStageCriteria(index, 'registrationsCount', e.target.value ? parseInt(e.target.value) : undefined)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                placeholder="0"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Min Events
                              </label>
                              <input
                                type="number"
                                value={stage.criteria.eventsAttended || ''}
                                onChange={(e) => updateStageCriteria(index, 'eventsAttended', e.target.value ? parseInt(e.target.value) : undefined)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                placeholder="0"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Max Days Since Activity
                              </label>
                              <input
                                type="number"
                                value={stage.criteria.lastActivityDays || ''}
                                onChange={(e) => updateStageCriteria(index, 'lastActivityDays', e.target.value ? parseInt(e.target.value) : undefined)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                placeholder="365"
                              />
                            </div>

                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={stage.criteria.servingHistory || false}
                                onChange={(e) => updateStageCriteria(index, 'servingHistory', e.target.checked)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <label className="ml-2 text-xs font-medium text-gray-600">
                                Requires Serving History
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={cancelForm}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={isEditing ? handleUpdateConfig : handleCreateConfig}
                    disabled={!formData.name || formData.stages.length === 0}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isEditing ? 'Update Configuration' : 'Create Configuration'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JourneyStageAdmin;
